import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsToggleRow } from '../components/SettingsSection';

interface PointSearchSectionProps {
  enabled: boolean;
}

/**
 * Toggles the address-search box in the checkout point picker. Saves
 * immediately on click; the nearby-points list always stays available.
 */
export function PointSearchSection({ enabled }: PointSearchSectionProps) {
  const { t } = useTranslation('settings');

  const update = useUpdateMerchantSettings();

  const [pending, setPending] = useState<boolean | null>(null);
  const checked = pending ?? enabled;

  useEffect(() => {
    if (pending !== null && pending === enabled) {
      setPending(null);
    }
  }, [pending, enabled]);

  const handleToggle = (next: boolean) => {
    if (update.isPending) return;
    setPending(next);
    update.mutate(
      { settings: { point_search_enabled: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPending(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsToggleRow
      label={t('checkout.pointSearch.label')}
      description={t('checkout.pointSearch.description')}
      checked={checked}
      onCheckedChange={handleToggle}
    />
  );
}
